import { useCallback } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { Base } from '@itsrever/design-system'
import { useUnsavedChangesGuard } from '@/hooks'
import { useDialog } from '@/ui/components/dialog/dialog-provider'

// Asks before a link leaves the page while active; a scope switch from the selector isn't a link.
function useLeaveGuard(active: boolean) {
  const { t } = useTranslation()
  const showDialog = useDialog()
  const confirmLeave = useCallback(
    () =>
      showDialog({
        title: t('save_bar.leave_title'),
        message: t('save_bar.leave_message'),
        okButtonText: t('save_bar.leave_ok'),
        cancelButtonText: t('save_bar.cancel')
      }),
    [showDialog, t]
  )
  useUnsavedChangesGuard(active, confirmLeave)
}

// Where every SaveBar floats: the page panel's bottom centre, whatever the page's own layout.
export const SAVE_BAR_SLOT_ID = 'save-bar-slot'

interface SaveBarProps {
  changes: number
  // What still blocks the save, shown beside the count.
  problem?: string
  // Asks before leaving the page; set it when there are edits the count can't see.
  guard?: boolean
  saveLabel?: string
  discardLabel?: string
  saving: boolean
  onDiscard: () => void
  onSave: () => void
}

// The floating bar pages save and discard their edits from; hidden while nothing is unsaved.
export const SaveBar: React.FC<
  SaveBarProps & {
    // Shows the bar before any change counts, e.g. for a record that was never saved.
    pending?: boolean
    editable?: boolean
  }
> = ({ pending = false, editable = true, ...props }) =>
  editable && (props.changes > 0 || pending || props.guard) ? (
    <FloatingBar {...props} />
  ) : null

const FloatingBar: React.FC<SaveBarProps> = ({
  changes,
  problem,
  guard = changes > 0,
  saveLabel,
  discardLabel,
  saving,
  onDiscard,
  onSave
}) => {
  const { t } = useTranslation()
  useLeaveGuard(guard)

  const bar = (
    <Base.SaveBar
      message={
        changes
          ? t('save_bar.unsaved', { count: changes })
          : t('save_bar.not_saved_yet')
      }
      problem={problem}
      saveLabel={saveLabel ?? t('save_bar.save_changes')}
      discardLabel={discardLabel ?? t('save_bar.discard')}
      saving={saving}
      onDiscard={onDiscard}
      onSave={onSave}
    />
  )
  // Outside the Layout (tests) there is no slot, so it renders in place.
  const slot = document.getElementById(SAVE_BAR_SLOT_ID)
  return slot ? createPortal(bar, slot) : bar
}
